// src/screens/EditarEnvioScreen.js
import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { db } from '../firebaseConfig';
import { doc, getDoc, updateDoc, deleteDoc, Timestamp } from 'firebase/firestore';
import {
  IoArrowBack,
  IoTrash,
  IoSave,
  IoTimeOutline,
  IoRefresh,
  // eslint-disable-next-line no-unused-vars
  IoInformationCircleOutline,
  IoCheckmarkCircle,
  IoAlertCircle,
  IoCubeOutline
} from 'react-icons/io5';
import KondoSidebar from '../components/Shared/KondoSidebar';
import KondoBottomNav from '../components/Shared/KondoBottomNav';
import SafeAreaInsets from '../components/Shared/SafeAreaInsets';
import KondoBrandHeader from '../components/Shared/KondoBrandHeader';
import './EditarEnvioScreen.css'; // --- Premium CSS ---

function EditarEnvioScreen() {
  const navigate = useNavigate();
  const { accesoId } = useParams();

  const [empresa, setEmpresa] = useState('');
  const [vigencia, setVigencia] = useState('1'); // Días a sumar
  const [activo, setActivo] = useState(true);
  const [vecesExtendido, setVecesExtendido] = useState(0);
  const [fechaExpira, setFechaExpira] = useState(null);
  const [esIlimitado, setEsIlimitado] = useState(true);
  const [usosMaximos, setUsosMaximos] = useState(1);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    const cargarDatos = async () => {
      const docRef = doc(db, "accesos", accesoId);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        const data = docSnap.data();
        setEmpresa(data.empresa);
        setVigencia('7');
        setActivo(data.activo !== false);
        setEsIlimitado(data.esIlimitado ?? (data.usosMaximos === -1));
        setUsosMaximos(data.usosMaximos && data.usosMaximos > 0 ? data.usosMaximos : 1);
        setVecesExtendido(data.vecesExtendido || 0);
        setFechaExpira(data.expira?.toDate());
        setLoading(false);
      } else {
        alert("Error: No se encontró el acceso.");
        navigate('/accesos/envios');
      }
    };
    cargarDatos();
  }, [accesoId, navigate]);

  // --- LOGICA DE GUARDAR CONFIG (ACTIVO / USOS / ESTADO) ---
  const handleSaveConfig = async (e) => {
    e.preventDefault();
    setProcessing(true);

    try {
      const docRef = doc(db, "accesos", accesoId);
      await updateDoc(docRef, {
        activo: activo,
        esIlimitado: esIlimitado,
        usosMaximos: esIlimitado ? -1 : (parseInt(usosMaximos, 10) || 1),
        nombreVisita: `Paquetería: ${empresa}`,
        estatus: activo ? 'Activo' : 'Inactivo'
      });
      alert('Configuración de paquetería actualizada.');
      navigate('/accesos/envios');
    } catch (error) {
      console.error("Error al actualizar: ", error);
      alert('Error al guardar cambios.');
    } finally {
      setProcessing(false);
    }
  };

  // --- LOGICA DE RENOVAR (NUEVO TOKEN) ---
  const handleRenovarTotal = async () => {
    if (!window.confirm("¿Generar un NUEVO código QR? El anterior dejará de funcionar.")) return;

    setProcessing(true);
    try {
      const docRef = doc(db, "accesos", accesoId);
      const dias = parseInt(vigencia, 10);
      const newToken = Math.floor(100000 + Math.random() * 900000).toString(); // 6 dígitos numéricos
      const newExpira = new Date(Date.now() + dias * 24 * 60 * 60 * 1000);

      await updateDoc(docRef, {
        token: newToken,
        expira: Timestamp.fromDate(newExpira),
        vigencia: dias,
        activo: true, // Reactivar si estaba inactivo
        vecesExtendido: 0 // Reset contador
      });

      alert(`Acceso Renovado.\nNuevo Token: ${newToken}`);
      navigate('/accesos/envios');
    } catch (e) {
      console.error(e);
      alert("Error al renovar.");
    } finally {
      setProcessing(false);
    }
  };

  // --- LOGICA DE EXTENDER (MISMO TOKEN) ---
  const handleExtender = async () => {
    const dias = parseInt(vigencia, 10);
    if (!window.confirm(`¿Extender vigencia por ${dias} días más (Mismo QR)?`)) return;

    setProcessing(true);
    try {
      const docRef = doc(db, "accesos", accesoId);
      // Calcular nueva fecha basada en HOY (si ya expiró) o sumar a la fecha actual?
      // Por seguridad, siempre desde HOY para evitar fechas absurdas si estaba expirado hace mucho.
      const newExpira = new Date(Date.now() + dias * 24 * 60 * 60 * 1000);

      await updateDoc(docRef, {
        expira: Timestamp.fromDate(newExpira),
        activo: true,
        vecesExtendido: vecesExtendido + 1
      });

      alert("Vigencia extendida exitosamente.");
      navigate('/accesos/envios');
    } catch (e) {
      console.error(e);
      alert("Error al extender.");
    } finally {
      setProcessing(false);
    }
  };

  const handleDelete = async () => {
    if (window.confirm('¿Borrar definitivamente este acceso?')) {
      setProcessing(true);
      try {
        await deleteDoc(doc(db, "accesos", accesoId));
        navigate('/accesos/envios');
      } catch (error) {
        console.error(error);
        alert('Error al borrar.');
      } finally {
        setProcessing(false);
      }
    }
  };

  if (loading) return <div className="edit-envio-container"><p>Cargando...</p></div>;

  const isExpired = fechaExpira && fechaExpira < new Date();
  const canExtend = true; // Permitir extender vigencia para conservar el código en Amazon

  return (
    <div className="ks-page">
      <KondoSidebar activeRoute="/accesos" />

      <main className="ks-main">
        <SafeAreaInsets top={false} bottom={true} />
        
        {/* Sticky Header unificado */}
        <div className="ks-header-sticky-wrapper">
          <KondoBrandHeader showBack={false} />
        </div>

        <div className="ks-scroll-area">
          <div className="edit-envio-container">
            {/* Sticky Compact Header */}
            <div className="edit-envio-fixed-wrapper">
              <div className="edit-envio-banner-compact">
                <button type="button" onClick={() => navigate('/accesos/envios')} className="edit-back-btn glass" aria-label="Volver">
                  <IoArrowBack size={24} />
                </button>
                <div className="edit-envio-banner-content">
                  <h2>Gestión de Paquetería</h2>
                  <p>Tus envíos y entregas</p>
                </div>
                <div className="edit-envio-banner-icon">
                  <IoCubeOutline />
                </div>
              </div>
            </div>

            <div className="edit-envio-card">
              {/* --- Status Widget --- */}
              <div className={`status-widget ${!isExpired ? 'valid-status' : 'expired-status'}`}>
                <div className="status-icon-box">
                  {!isExpired ? <IoCheckmarkCircle color="#059669" /> : <IoAlertCircle color="#dc2626" />}
                </div>
                <div className="status-details">
                  <h4>{empresa}</h4>
                  <p>
                    {isExpired
                      ? `Expiró el ${fechaExpira?.toLocaleDateString()}`
                      : `Válido hasta ${fechaExpira?.toLocaleDateString()}`
                    }
                  </p>
                  <p style={{ fontSize: '0.75rem' }}>
                    {esIlimitado ? '✓ Entregas Ilimitadas' : `Límite de usos: ${usosMaximos}`}
                  </p>
                </div>
              </div>

              <form onSubmit={handleSaveConfig} className="edit-form">
                <div className="form-group">
                  <p className="form-section-title">CONFIGURACIÓN GENERAL</p>
                </div>

                {/* Activo Switch */}
                <div className="form-group" style={{ background: '#f1f5f9', padding: 12, borderRadius: 12, display: 'flex', alignItems: 'center', gap: 12 }}>
                  <input
                    type="checkbox"
                    checked={activo}
                    onChange={(e) => setActivo(e.target.checked)}
                    style={{ width: 20, height: 20 }}
                  />
                  <div style={{ flex: 1 }}>
                    <label style={{ fontWeight: 600, fontSize: '0.9rem', color: '#334155' }}>Acceso Habilitado</label>
                    <div style={{ fontSize: '0.75rem', color: '#64748b' }}>Desactívalo para bloquear la entrada temporalmente.</div>
                  </div>
                </div>

                {/* Usos Ilimitados / Configurable */}
                <div style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '12px', padding: '12px', marginBottom: '16px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px' }}>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: '0.85rem', color: '#1e293b' }}>Entregas / Usos Permitidos</div>
                      <div style={{ fontSize: '0.72rem', color: '#64748b' }}>Evita regenerar códigos para tus compras de Amazon.</div>
                    </div>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', background: esIlimitado ? '#ecfdf5' : '#f1f5f9', border: `1px solid ${esIlimitado ? '#10b981' : '#cbd5e1'}`, padding: '5px 10px', borderRadius: '20px', fontWeight: 700, fontSize: '0.75rem', color: esIlimitado ? '#047857' : '#64748b' }}>
                      <input
                        type="checkbox"
                        checked={esIlimitado}
                        onChange={(e) => setEsIlimitado(e.target.checked)}
                        style={{ accentColor: '#10b981', width: '15px', height: '15px' }}
                      />
                      <span>{esIlimitado ? '✓ Ilimitado' : 'Limitar'}</span>
                    </label>
                  </div>

                  {!esIlimitado && (
                    <div style={{ marginTop: '10px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <label style={{ fontSize: '0.8rem', color: '#475569', fontWeight: 600 }}>Entregas máximas:</label>
                      <input
                        type="number"
                        min="1"
                        max="50"
                        value={usosMaximos}
                        onChange={(e) => setUsosMaximos(parseInt(e.target.value, 10) || 1)}
                        className="form-input"
                        style={{ width: '80px', padding: '5px 8px' }}
                      />
                    </div>
                  )}
                </div>

                <button type="submit" className="btn-save" disabled={processing}>
                  <IoSave size={18} />
                  Guardar Configuración
                </button>

                <div style={{ borderBottom: '1px dashed #cbd5e1', margin: '16px 0' }}></div>

                <div className="form-group">
                  <p className="form-section-title">GESTIÓN DE VIGENCIA</p>
                  <label className="form-label" style={{ marginTop: 12 }}>Días a añadir / renovar</label>
                  <select value={vigencia} onChange={(e) => setVigencia(e.target.value)} className="form-select">
                    <option value="1">1 Día</option>
                    <option value="7">7 Días</option>
                    <option value="30">30 Días</option>
                  </select>
                </div>

                <div className="actions-grid">
                  {/* Botón Extender: Conservar el mismo token de Amazon */}
                  <button type="button" className="btn-extend" onClick={handleExtender} disabled={processing}>
                    <IoTimeOutline size={18} />
                    Extender Vigencia (Mismo QR/PIN)
                  </button>

                  {/* Botón Renovar: Solo si el usuario expresamente quiere un código nuevo */}
                  <button type="button" className="btn-renew" onClick={handleRenovarTotal} disabled={processing}>
                    <IoRefresh size={18} />
                    Generar Nuevo QR
                  </button>
                </div>

                <button type="button" className="btn-delete" onClick={handleDelete} disabled={processing}>
                  <IoTrash size={18} />
                  Borrar Acceso
                </button>
              </form>
            </div>
          </div>
        </div>

        <KondoBottomNav activeRoute="/accesos" />
      </main>
    </div>
  );
}

export default EditarEnvioScreen;